import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Lock, CreditCard, RefreshCw, Check, ArrowRight, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAcademyStore } from '../../../store/academyStore';

const STEPS = [
    { id: 1, title: 'The Badge Maker', desc: 'First, we need to know who you are!' },
    { id: 2, title: 'The P.A.S.S.W.O.R.D Blender', desc: 'We never save your real password. We blend it!' },
    { id: 3, title: 'Salt & Pepper', desc: 'Adding random junk to make it impossible to guess.' },
    { id: 4, title: 'The Token', desc: 'Your digital wristband for the amusement park.' }
];

export const BadgeBuilder = ({ onComplete }: { onComplete: () => void }) => {
    const [name, setName] = useState('');
    const [role, setRole] = useState('Cadet');
    const [showCard, setShowCard] = useState(false);

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-xl border border-gray-100">
                <h3 className="text-2xl font-bold font-comic mb-4 text-center">📇 Build Your ID</h3>

                <div className="space-y-4">
                    <div>
                        <label className="block font-bold text-gray-500 text-sm mb-1">Codename</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="w-full border-2 border-gray-200 rounded-xl p-3 text-lg font-bold outline-none focus:border-blue-500"
                            placeholder="e.g. Agent Banana"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        {['Cadet', 'Captain', 'Ninja', 'Wizard'].map(r => (
                            <button
                                key={r}
                                onClick={() => setRole(r)}
                                className={`p-2 rounded-lg font-bold text-sm border-2 transition-all ${role === r ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-gray-100 text-gray-400'}`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => setShowCard(true)}
                        disabled={!name}
                        className="w-full bg-green-500 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-green-600 disabled:opacity-50 transition-transform active:scale-95"
                    >
                        Print Badge
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {showCard && (
                    <motion.div
                        initial={{ scale: 0, rotate: -10 }}
                        animate={{ scale: 1, rotate: 0 }}
                        className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-6 rounded-3xl shadow-2xl w-80 relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-20">
                            <Shield size={100} />
                        </div>
                        <div className="flex flex-col items-center relative z-10">
                            <div className="w-20 h-20 bg-white rounded-full mb-4 border-4 border-yellow-400 overflow-hidden p-1">
                                <img src="/assets/gorillas/gorilla_security.png" className="w-full h-full object-cover rounded-full bg-blue-100" />
                            </div>
                            <h2 className="text-2xl font-black font-comic tracking-wider">{name}</h2>
                            <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold mt-2">
                                Rank: {role}
                            </div>
                            <div className="mt-6 w-full bg-white/10 rounded p-2 flex items-center justify-between">
                                <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></div>
                                <span className="font-mono text-xs opacity-70">ID: {Math.floor(Math.random() * 999999)}</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {showCard && (
                <button onClick={onComplete} className="bg-blue-600 text-white font-bold px-8 py-3 rounded-full shadow-xl animate-bounce">
                    Next: Secure It! ➡️
                </button>
            )}
        </div>
    );
};

export const PasswordBlender = ({ onComplete }: { onComplete: () => void }) => {
    const [password, setPassword] = useState('');
    const [isBlending, setIsBlending] = useState(false);
    const [hashed, setHashed] = useState('');

    const blend = () => {
        setIsBlending(true);
        setHashed('');
        setTimeout(() => {
            setIsBlending(false);
            // Simple fake hash visualization
            setHashed(btoa(password).split('').reverse().join('') + 'xyz');
        }, 2000);
    };

    return (
        <div className="flex flex-col md:flex-row items-center justify-center gap-12 w-full">
            <div className="w-full max-w-sm space-y-4">
                <h3 className="text-2xl font-bold font-comic">The P.A.S.S.W.O.R.D Blender</h3>
                <p className="text-gray-600">Enter a password. We will put it in the blender so even WE don't know what it is!</p>

                <input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Type a secret..."
                    className="w-full border-2 border-gray-300 rounded-xl p-4 text-xl font-bold outline-none focus:border-orange-500"
                />

                <button
                    onClick={blend}
                    disabled={!password || isBlending}
                    className="w-full bg-orange-500 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-orange-600 disabled:opacity-50 text-xl flex items-center justify-center gap-2"
                >
                    {isBlending ? <RefreshCw className="animate-spin" /> : '🌪️ BLEND IT!'}
                </button>
            </div>

            <div className="relative w-80 h-80 flex items-center justify-center">
                {isBlending ? (
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 0.2 }}
                        className="text-9xl"
                    >
                        🌪️
                    </motion.div>
                ) : hashed ? (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="bg-gray-800 text-green-400 p-6 rounded-xl font-mono break-all text-center shadow-2xl border-4 border-gray-600"
                    >
                        <div className="text-xs text-gray-500 mb-2">OUTPUT (Store this in DB)</div>
                        {hashed}
                    </motion.div>
                ) : (
                    <img src="/assets/gorillas/gorilla_chef.png" className="w-full h-full object-contain opacity-80" onError={(e) => e.currentTarget.style.display = 'none'} />
                )}
            </div>

            {hashed && (
                <div className="fixed bottom-8 right-8">
                    <button onClick={onComplete} className="bg-green-600 text-white font-bold px-8 py-3 rounded-full shadow-xl animate-bounce flex items-center gap-2">
                        I Get It! Next <ArrowRight />
                    </button>
                </div>
            )}
        </div>
    );
}

export const AuthModule = () => {
    const { completeModule } = useAcademyStore();
    const [step, setStep] = useState(1);
    const [completed, setCompleted] = useState(false);

    const handleFinish = () => {
        if (!completed) {
            setCompleted(true);
            completeModule('auth', 100, 'badge_cyber-security');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <div className="p-4 bg-white shadow-sm flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                    <Shield className="text-blue-600" />
                    <h1 className="font-bold text-xl font-comic">Guardians of the Gate</h1>
                </div>
                <Link to="/" className="text-sm font-bold text-gray-500 hover:text-green-600 flex items-center gap-1">
                    <Home size={16} /> Dashboard
                </Link>
            </div>

            <div className="flex-1 flex flex-col items-center p-8">
                {/* Progress */}
                <div className="flex gap-2 mb-12">
                    {STEPS.map((s, i) => (
                        <div key={s.id} className={`h-2 w-16 rounded-full transition-all ${step >= s.id ? 'bg-blue-600' : 'bg-gray-200'}`} />
                    ))}
                </div>

                {step === 1 && <BadgeBuilder onComplete={() => setStep(2)} />}
                {step === 2 && <PasswordBlender onComplete={() => setStep(3)} />}

                {step === 3 && (
                    <div className="text-center space-y-6 max-w-2xl">
                        <h2 className="text-4xl font-bold font-comic">🧂 Adding Salt & Pepper</h2>
                        <p className="text-xl text-gray-600">
                            If two people have the password "monkey123", their hash looks the same!
                            That's bad. So we sprinkle random computer gibberish (Salt) before blending.
                        </p>
                        <div className="bg-white p-8 rounded-2xl shadow-xl flex items-center justify-center gap-4 text-2xl font-mono font-bold">
                            <span className="text-gray-400">"monkey123"</span>
                            <span>+</span>
                            <span className="text-pink-500 animate-pulse">"x9s8d7"</span>
                            <span>=</span>
                            <span className="text-blue-600">Unique Hash!</span>
                        </div>
                        <button onClick={() => setStep(4)} className="bg-blue-600 text-white font-bold px-8 py-3 rounded-full shadow-lg">Next</button>
                    </div>
                )}

                {step === 4 && (
                    <div className="text-center space-y-6">
                        <h2 className="text-4xl font-bold font-comic">🎟️ The Token</h2>
                        <p className="text-xl text-gray-600">
                            You don't show your Badge every time you walk 5 feet. You get a wristband (Token).
                            <br />
                            This token expires (like a ticket) so if you lose it, no one can use it forever.
                        </p>

                        <div className="py-12">
                            <Link to="/" onClick={handleFinish} className="bg-green-600 text-white text-xl font-bold px-12 py-4 rounded-full shadow-2xl hover:bg-green-700 transition-transform hover:scale-105">
                                🎓 Collect Badge & Finish Module
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthModule;
